import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import parinayFigma from '../assets/images/parinay-figma.png';

const BRAND_GRADIENT = "linear-gradient(135deg, #0047ff, #00c4ff)";

/* ===================== WRAPPER ===================== */

const Wrapper = styled.div`
  font-family: 'Poppins', sans-serif;
  min-height: 100vh;
  color: #e5e7eb;
  padding: 80px 16px 100px;
  overflow-x: hidden;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

/* ===================== HEADER ===================== */

const Title = styled(motion.h1)`
  text-align: center;
  font-size: clamp(2.2rem, 5vw, 3.2rem);
  margin-bottom: 20px;

  background: ${BRAND_GRADIENT};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Description = styled(motion.p)`
  max-width: 850px;
  margin: 0 auto 60px;
  text-align: center;
  font-size: 1.05rem;
  line-height: 1.7;
  color: #cbd5e1;
`;

/* ===================== SERVICES GRID ===================== */

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  justify-items: center;
  padding: 0 12px;

  @media (max-width: 360px) {
    grid-template-columns: 1fr;
    padding: 0 8px;
  }
`;

const Card = styled(motion.div)`
  width: 100%;
  max-width: 320px;

  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(14px);
  border-radius: 16px;
  padding: 20px;

  box-shadow: 0 10px 30px rgba(0,0,0,0.25);
  border: 1px solid rgba(255,255,255,0.12);
`;

const CardTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 10px;

  background: ${BRAND_GRADIENT};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const CardDesc = styled.p`
  font-size: 0.95rem;
  line-height: 1.6;
  color: #cbd5e1;
`;

/* ===================== FIGMA SECTION ===================== */

const FigmaSection = styled(motion.section)`
  margin-top: 100px;
  text-align: center;
`;

const FigmaCard = styled.div`
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(16px);
  border-radius: 20px;
  color: #e5e7eb;
  padding: 40px 30px;
  max-width: 920px;
  margin: 0 auto;

  box-shadow: 0 18px 30px rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255,255,255,0.12);
`;

const FigmaImage = styled.img`
  width: 100%;
  max-width: 700px;
  border-radius: 16px;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.35);
  margin-top: 30px;
  display: block;
  margin-left: auto;
  margin-right: auto;
`;

/* ===================== DATA ===================== */

const services = [
  {
    title: 'Wireframing',
    desc: 'We create wireframes to visualize app structure and layout before design begins.',
  },
  {
    title: 'User Interface Design',
    desc: 'Pixel-perfect designs with consistent branding and intuitive UI patterns.',
  },
  {
    title: 'User Experience Research',
    desc: 'Understanding your users to design experiences that convert and delight.',
  },
  {
    title: 'Prototyping & Interactions',
    desc: 'Interactive mockups to test and iterate before development.',
  },
  {
    title: 'Mobile & Web Consistency',
    desc: 'Responsive designs tailored to both mobile and desktop for best performance.',
  },
];

/* ===================== COMPONENT ===================== */

export default function UIUX() {
  return (
    <Wrapper>
      <Container>

        <Title
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          UI/UX Design Services
        </Title>

        <Description
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
        >
          We design intuitive, engaging, and visually compelling interfaces that align with your
          brand. From wireframes to full design systems, we ensure every user interaction is seamless.
        </Description>

        <Grid>
          {services.map((item, index) => (
            <Card
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              whileHover={{
                y: -10,
                scale: 1.04,
                boxShadow: "0 0 30px rgba(0,196,255,0.3)",
              }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            >
              <CardTitle>{item.title}</CardTitle>
              <CardDesc>{item.desc}</CardDesc>
            </Card>
          ))}
        </Grid>

        {/* Figma Showcase */}
        <FigmaSection
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <FigmaCard>
            <h2
              style={{
                fontSize: '1.8rem',
                marginBottom: '16px',
                background: BRAND_GRADIENT,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Parinay App – Figma UI Showcase
            </h2>

            <p style={{ fontSize: '1rem', lineHeight: '1.6', color: '#cbd5e1' }}>
              The Parinay app’s UI/UX was meticulously designed in Figma, combining bold modern aesthetics
              with an intuitive flow. Every screen—from onboarding to matchmaking—was wireframed and tested
              for usability. With consistent color harmony, spacing, and minimal clutter, the design achieves
              both beauty and efficiency. The final Figma prototype showcased seamless transitions, mobile responsiveness,
              and a perfect blend of elegance and function.
            </p>

            <FigmaImage src={parinayFigma} alt="Parinay Figma Design" />
          </FigmaCard>
        </FigmaSection>

      </Container>
    </Wrapper>
  );
}
